import React, { FC } from 'react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import { IconName } from '@shinetools/sunshine-icons';

import { CapitalDepositStatus } from '__generated__/GQL';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import SunshineTag from 'components/_core/SunshineTag';
import TimelinePresentation from 'features/Onboarding/components/TimelinePresentation';

import locales from './locales';

interface StepConfiguration {
  activeStatuses: CapitalDepositStatus[];
  description: string;
  iconName: IconName;
  title: string;
}
const stepsConfiguration: StepConfiguration[] = [
  {
    activeStatuses: [
      CapitalDepositStatus.Initiated,
      CapitalDepositStatus.FormCompleted,
    ],
    description: locales.steps.companyStructure.description,
    iconName: 'misc',
    title: locales.steps.companyStructure.title,
  },
  {
    activeStatuses: [
      CapitalDepositStatus.ApplicationSubmitted,
      CapitalDepositStatus.KycSentForPrecheck,
      CapitalDepositStatus.KycPrechecked,
      CapitalDepositStatus.KycSentToTreezor,
      CapitalDepositStatus.KycSentForPostcheck,
      CapitalDepositStatus.KycValidated,
      CapitalDepositStatus.KycInvalid,
    ],
    description: locales.steps.legalVerification.description,
    iconName: 'shield',
    title: locales.steps.legalVerification.title,
  },
  {
    activeStatuses: [
      CapitalDepositStatus.IbanstartAccountCreated,
      CapitalDepositStatus.ProviderAccountCreated,
    ],
    description: locales.steps.fundsDeposit.description,
    iconName: 'euro',
    title: locales.steps.fundsDeposit.title,
  },
  {
    activeStatuses: [
      CapitalDepositStatus.FundsReceived,
      CapitalDepositStatus.CertificateOfDepositSent,
    ],
    description: locales.steps.companyCreation.description,
    iconName: 'download',
    title: locales.steps.companyCreation.title,
  },
  {
    activeStatuses: [
      CapitalDepositStatus.CompanyCreated,
      CapitalDepositStatus.KybSentForPrecheck,
      CapitalDepositStatus.KybPrechecked,
      CapitalDepositStatus.KybSentToTreezor,
      CapitalDepositStatus.KybInvalid,
      CapitalDepositStatus.KybValidated,
      CapitalDepositStatus.KybSentForPostcheck,
      CapitalDepositStatus.FundsReleaseInitiated,
    ],
    description: locales.steps.fundsRelease.description,
    iconName: 'unlock',
    title: locales.steps.fundsRelease.title,
  },
];

type NextStepInfoModalProps = Pick<
  UseDisclosureReturn,
  'isOpen' | 'onClose'
> & {
  capitalDepositStatus: CapitalDepositStatus;
};
const NextStepInfoModal: FC<NextStepInfoModalProps> = ({
  capitalDepositStatus,
  isOpen,
  onClose,
}) => {
  const getSteps = () => {
    const activeStepIndex =
      stepsConfiguration.findIndex((stepConfiguration) =>
        stepConfiguration.activeStatuses.includes(capitalDepositStatus),
      ) || 0;

    return stepsConfiguration.map((stepConfiguration, index) => {
      const { description, iconName, title } = stepConfiguration;

      const isActiveStep = index === activeStepIndex;
      const isStepDone = index < activeStepIndex;

      const getColorScheme = () => {
        if (isStepDone) {
          return 'green';
        }
        if (isActiveStep) {
          return 'yellow';
        }
        return 'grey';
      };

      return {
        description,
        extraContent: isActiveStep ? (
          <SunshineTag colorScheme="yellow">{locales.inProgress}</SunshineTag>
        ) : undefined,
        icon: (
          <SquircleIcon
            colorScheme={getColorScheme()}
            name={isStepDone ? 'tick' : iconName}
          />
        ),
        title,
      };
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
      <ModalOverlay />

      <ModalContent
        alignSelf={{ base: 'flex-end', md: 'center' }}
        borderBottomRadius={{ base: 0, md: 'radius-8' }}
        borderTopRadius={{ base: 'radius-16', md: 'radius-8' }}
        marginY={{ base: 0, md: '3.75rem' }}
      >
        <ModalBody>
          <TimelinePresentation
            description={locales.description}
            marginY="space-40"
            steps={getSteps()}
            title={locales.title}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default NextStepInfoModal;
